import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, FloppyDisk, UsersThree, Crown } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "../../lib/api";

const DEFAULT_ACTIONS = ["extend_trial", "grant_pro", "cancel_pro"];
const ALL_ACTIONS = ["extend_trial", "grant_pro", "cancel_pro"]; // promote/demote intentionally NOT offered

const blankDraft = {
  name: "",
  slug: "",
  description: "",
  allowed_features: [],
  allowed_actions: [...DEFAULT_ACTIONS],
};

export default function NodesPanel({ users = [], onRefreshUsers }) {
  const [nodes, setNodes] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // node id or "new"
  const [draft, setDraft] = useState(blankDraft);
  const [assigning, setAssigning] = useState(null); // {nodeId} when picking node-admin

  // Memoise the assign-admin candidate list so we don't re-filter on every render.
  const assignableUsers = useMemo(
    () => users.filter((u) => u.role !== "admin"),
    [users]
  );

  const load = async () => {
    setLoading(true);
    try {
      const [n, p] = await Promise.all([
        api.get("/admin/nodes"),
        api.get("/billing/pricing"),
      ]);
      setNodes(n.data);
      setPricing(p.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startNew = () => { setDraft(blankDraft); setEditing("new"); };
  const startEdit = (n) => {
    setDraft({
      name: n.name,
      slug: n.slug,
      description: n.description || "",
      allowed_features: n.allowed_features || [],
      allowed_actions: n.allowed_actions || [...DEFAULT_ACTIONS],
    });
    setEditing(n.id);
  };

  const save = async () => {
    try {
      if (editing === "new") {
        await api.post("/admin/nodes", draft);
        toast.success("Node created.");
      } else {
        const { slug, ...patch } = draft;
        await api.put(`/admin/nodes/${editing}`, patch);
        toast.success("Node updated.");
      }
      setEditing(null);
      await load();
      onRefreshUsers?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete node "${name}"? Users will be unassigned (data kept) and the node admin demoted to user.`)) return;
    try {
      await api.delete(`/admin/nodes/${id}`);
      toast.success("Node deleted.");
      await load();
      onRefreshUsers?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const assignAdmin = async (nodeId, userId) => {
    try {
      await api.post(`/admin/nodes/${nodeId}/assign-admin`, { user_id: userId });
      toast.success("Node admin assigned.");
      setAssigning(null);
      await load();
      onRefreshUsers?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (loading) return <div className="text-muted-foreground" data-testid="admin-nodes-loading">Loading…</div>;

  const allAddonSlugs = (pricing?.addons || []).flatMap((a) => [a.slug, ...((a.sub_features || []).map((s) => s.slug))]);

  return (
    <div className="space-y-6" data-testid="admin-nodes-panel">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-serif text-2xl mb-1">Nodes</h2>
          <p className="text-xs text-muted-foreground max-w-xl">
            Create a node, assign features and allowed admin actions, then promote a user to be the
            nodal admin. The nodal admin sees and manages only users in their node.
          </p>
        </div>
        <button onClick={startNew} className="btn-primary px-4 py-2 text-sm inline-flex items-center gap-2" data-testid="admin-node-new">
          <Plus size={14} weight="bold" /> New node
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {nodes.length === 0 && (
          <div className="card-flat p-8 text-sm text-muted-foreground col-span-full" data-testid="admin-nodes-empty">
            No nodes yet. Create one to group users (eg. by city, broker franchise, employer).
          </div>
        )}
        {nodes.map((n) => (
          <div key={n.id} className="card-flat p-5" data-testid={`admin-node-card-${n.id}`}>
            <div className="flex items-start justify-between mb-3 gap-3">
              <div className="min-w-0">
                <div className="font-serif text-xl truncate">{n.name}</div>
                <div className="text-[10px] font-mono text-muted-foreground">{n.slug}</div>
              </div>
              <div className="flex gap-1.5 shrink-0">
                <button onClick={() => startEdit(n)} className="btn-ghost p-1.5" data-testid={`admin-node-edit-${n.id}`}><PencilSimple size={14} /></button>
                <button onClick={() => remove(n.id, n.name)} className="btn-ghost p-1.5 text-[hsl(var(--primary))]" data-testid={`admin-node-delete-${n.id}`}><Trash size={14} /></button>
              </div>
            </div>
            {n.description && <p className="text-xs text-muted-foreground mb-3">{n.description}</p>}
            <div className="space-y-2 text-xs">
              <div className="flex items-center gap-2">
                <UsersThree size={14} weight="duotone" className="text-[hsl(var(--secondary))]" />
                <span>{n.user_count} user{n.user_count === 1 ? "" : "s"} assigned</span>
              </div>
              <div className="flex items-start gap-2">
                <Crown size={14} weight="duotone" className="text-[hsl(var(--secondary))] mt-0.5" />
                <div className="min-w-0">
                  {n.node_admin_email ? (
                    <span className="truncate" data-testid={`admin-node-admin-${n.id}`}>{n.node_admin_email}</span>
                  ) : (
                    <button onClick={() => setAssigning({ nodeId: n.id })} className="text-[hsl(var(--secondary))] hover:text-foreground underline" data-testid={`admin-node-assign-admin-${n.id}`}>
                      Assign nodal admin
                    </button>
                  )}
                  {n.node_admin_email && (
                    <button onClick={() => setAssigning({ nodeId: n.id })} className="block text-[10px] text-muted-foreground hover:text-foreground" data-testid={`admin-node-reassign-admin-${n.id}`}>
                      reassign…
                    </button>
                  )}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Features unlocked for node users</div>
                <div className="flex flex-wrap gap-1">
                  {(n.allowed_features || []).length === 0 && <span className="text-muted-foreground">—</span>}
                  {(n.allowed_features || []).map((f) => (
                    <span key={f} className="bg-[hsl(var(--muted))] px-1.5 py-0.5 text-[10px] rounded-sm">{f}</span>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Allowed admin actions</div>
                <div className="flex flex-wrap gap-1">
                  {(n.allowed_actions || []).map((a) => (
                    <span key={a} className="bg-[hsl(var(--muted))] px-1.5 py-0.5 text-[10px] rounded-sm">{a}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="card-flat p-6" data-testid="admin-node-editor">
          <div className="eyebrow mb-3">{editing === "new" ? "New node" : "Edit node"}</div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Name" value={draft.name} onChange={(v) => setDraft({ ...draft, name: v })} testid="admin-node-name" />
            <Field label="Slug (lowercase, no spaces)" value={draft.slug} onChange={(v) => setDraft({ ...draft, slug: v })} disabled={editing !== "new"} testid="admin-node-slug" />
            <div className="sm:col-span-2">
              <label className="text-sm block">
                <span className="block text-xs text-muted-foreground mb-1">Description (optional)</span>
                <textarea value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} rows={2} className="w-full bg-background border hairline px-3 py-2 text-sm" data-testid="admin-node-desc" />
              </label>
            </div>
            <div className="sm:col-span-2">
              <div className="text-xs text-muted-foreground mb-2">Features unlocked for users in this node (on top of their plan)</div>
              <div className="flex flex-wrap gap-2">
                {allAddonSlugs.map((slug) => (
                  <label key={slug} className="text-xs inline-flex items-center gap-1.5 border hairline px-2 py-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={draft.allowed_features.includes(slug)}
                      onChange={(e) => setDraft({
                        ...draft,
                        allowed_features: e.target.checked
                          ? [...draft.allowed_features, slug]
                          : draft.allowed_features.filter((s) => s !== slug),
                      })}
                      data-testid={`admin-node-feature-${slug}`}
                    />
                    <span>{slug}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="sm:col-span-2">
              <div className="text-xs text-muted-foreground mb-2">Allowed admin actions (what the nodal admin can do)</div>
              <div className="flex flex-wrap gap-2">
                {ALL_ACTIONS.map((a) => (
                  <label key={a} className="text-xs inline-flex items-center gap-1.5 border hairline px-2 py-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={draft.allowed_actions.includes(a)}
                      onChange={(e) => setDraft({
                        ...draft,
                        allowed_actions: e.target.checked
                          ? [...draft.allowed_actions, a]
                          : draft.allowed_actions.filter((x) => x !== a),
                      })}
                      data-testid={`admin-node-action-${a}`}
                    />
                    <span>{a}</span>
                  </label>
                ))}
              </div>
              <div className="text-[10px] text-muted-foreground mt-1">Promote / Demote are never available to nodal admins.</div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={() => setEditing(null)} className="btn-ghost px-3 py-2 text-xs" data-testid="admin-node-cancel">Cancel</button>
            <button onClick={save} className="btn-primary px-3 py-2 text-xs inline-flex items-center gap-1" data-testid="admin-node-save">
              <FloppyDisk size={12} weight="duotone" /> Save node
            </button>
          </div>
        </div>
      )}

      {assigning && (
        <div className="card-flat p-6" data-testid="admin-node-assign-modal">
          <div className="eyebrow mb-3">Assign nodal admin</div>
          <p className="text-xs text-muted-foreground mb-3">
            Pick a user to promote. They will gain admin powers ONLY for users in this node.
          </p>
          <div className="max-h-72 overflow-auto border hairline">
            {assignableUsers.map((u) => (
              <button
                key={u.id}
                onClick={() => assignAdmin(assigning.nodeId, u.id)}
                className="w-full text-left px-3 py-2 text-xs hover:bg-[hsl(var(--muted))] flex items-center justify-between border-b hairline last:border-b-0"
                data-testid={`admin-node-pick-user-${u.id}`}
              >
                <span>{u.name || u.email.split("@")[0]} <span className="text-muted-foreground">· {u.email}</span></span>
                <span className="text-[10px] text-muted-foreground">{u.role}{u.node_name ? ` · ${u.node_name}` : ""}</span>
              </button>
            ))}
          </div>
          <div className="flex justify-end mt-4">
            <button onClick={() => setAssigning(null)} className="btn-ghost px-3 py-2 text-xs" data-testid="admin-node-assign-cancel">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", disabled, testid }) {
  return (
    <label className="text-sm block">
      <span className="block text-xs text-muted-foreground mb-1">{label}</span>
      <input
        type={type}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-background border hairline px-3 py-2 text-sm disabled:opacity-60"
        data-testid={testid}
      />
    </label>
  );
}
